import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {environment} from 'src/environments/environment';
import {v1 as uuidv1} from 'uuid';
import {ApiService} from '../../../shared/services/api.service';
import {RequestService} from '../../../shared/services/request.service';
import {FormService, LangService, LoaderService, NavigateService} from 'ngx-satoris';
import {PlacesService} from '../../../shared/services/places.service';
import {UserPermission} from '../../../shared/models/user';
import {Place} from '../../../shared/models/place';

@Component({
  selector: 'app-place',
  templateUrl: './place.component.html',
  styleUrls: ['./place.component.scss']
})
export class PlaceComponent implements OnInit {
  placeId: string;
  isNewPlace = false;
  canEdit = false;
  form: FormGroup;
  submitted = false;
  allPlaces: { name: string; value: string }[] = [];
  placesSO: { name: string; value: string }[] = [];
  countriesSelected: string[] = [];
  dataLoaded = false;

  readonly environment = environment;
  readonly DocumentType = DocumentType;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    public api: ApiService,
    public request: RequestService,
    public nav: NavigateService,
    private loader: LoaderService,
    public forms: FormService,
    private lang: LangService,
    public place: PlacesService) {
    const id = this.route.snapshot.paramMap.get('placeId');
    if(id && id !== 'new') {
      this.placeId = id;
    } else if(id === 'new') {
      this.isNewPlace = true;
    }
    this.initForm();
  }

  initForm() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      id: [this.placeId || uuidv1(), [Validators.required, Validators.maxLength(50)]],
      connectUAPattern: ['.*', [Validators.required]],
      connectIPRange: ['0.0.0.0/0', [Validators.required]],
      externalCode: ['', [Validators.pattern('^[A-Z]{3,4}$')]],
      openingDays: [[]],
      openingHours: [[]],
      closedDays: [[]],
      modeLocal: ['closed'],
      address: ['']
    });

    if(this.placeId) {
      this.form.addControl('phoneNumbersCsv', this.formBuilder.control('', [Validators.maxLength(20)]));
      this.form.addControl('idParent', this.formBuilder.control(''));
      this.form.addControl('dailyOtp', this.formBuilder.control(''));
      this.form.get('id')?.disable();
    } else {
      this.form.addControl('placesParent', this.formBuilder.control(''));
    }
  }

  ngOnInit() {
    this.place.getAllPlaces().then(() => {
      this.allPlaces = this.transformSelect(this.place.allPlaces);
      if(this.api.userRole.isAdmin) {
        this.placesSO = this.allPlaces;
      } else {
        const adminPlaceIds = this.api.userInfo?.links?.filter(l => l.allowAdmin).map(l => l.place_id) ?? [];
        this.placesSO = this.allPlaces.filter(p => adminPlaceIds.includes(p.value));
        if(!this.placeId) {
          this.form.get('placesParent').setValidators([Validators.required]);
          this.form.get('placesParent').updateValueAndValidity();
        }
      }

      if(this.placeId) {
        return this.place.getPlace(this.placeId, true, true, 0);
      }

      return null;
    }).then(() => {
      if(this.placeId) {
        this.updateFormWithPlaceData();
        this.form.get('id').disable();
        this.form.get('idParent').disable();
        this.canEdit = (this.api.userPlaceId === this.placeId && this.api.userRole.isWorker && this.api.hasPerm(UserPermission.ALLOW_ADMIN)) || this.api.userRole.isAdmin;
      } else {
        this.canEdit = true;
      }
      this.dataLoaded = true;
    }).catch(() => this.nav.to('places'));
  }

  updateFormWithPlaceData() {
    if(this.place.currentPlace) {
      this.form.patchValue({
        name: this.place.currentPlace.longName,
        id: this.place.currentPlace.id,
        connectUAPattern: this.place.currentPlace.connectUAPattern,
        connectIPRange: this.place.currentPlace.connectIPRange,
        externalCode: this.place.currentPlace.externalCode,
        phoneNumbersCsv: this.place.currentPlace.phoneNumbersCsv,
        idParent: this.place.currentPlace.parent_id,
        openingHours: this.place.currentPlace.openingHours,
        openingDays: Array.isArray(this.place.currentPlace.openingDays) ? this.place.currentPlace.openingDays.filter(d => d !== null && d !== undefined): [],
        closedDays: Array.isArray(this.place.currentPlace.closedDays) ? this.place.currentPlace.closedDays.filter(d => d !== null && d !== undefined && d !== '') : [],
        modeLocal: (this.place.currentPlace as any)?.modeLocal || 'closed',
        address: this.place.currentPlace.address || ''
      });

      if(this.place.currentPlace.specifiedNationals) {
        this.countriesSelected = [...this.place.currentPlace.specifiedNationals];
      }
    }
  }

  submitForm() {
    this.submitted = true;
    if(this.forms.getFormErrors(this.form).length < 1) {
      this.loader.loading(true);
      if(this.placeId) {
        if(this.api.env.useGuichet){
          this.loader.loading(true, {type: 'warn', message: this.lang.transform('place.warn_closure'), btnLabel: this.lang.transform('global.confirm'), custom: {closeBtnLabel: this.lang.transform('global.cancel')}}).then((confirm: boolean) => {
            if(confirm) {
              this.updatePlace();
            }
          });
        } else {
          this.updatePlace();
        }
      } else {
        this.createPlace();
      }
    }
  }

  private updatePlace() {
    const place: Place = {
      longName: this.form.get('name')?.value,
      phoneNumbersCsv: this.form.get('phoneNumbersCsv')?.value || '',
      connectUAPattern: this.form.get('connectUAPattern')?.value || '.*',
      connectIPRange: this.form.get('connectIPRange')?.value || '0.0.0.0/0',
      parent_id: this.form.get('idParent')?.value || '',
      id: this.place?.currentPlace?.id,
      externalCode: this.form.get('externalCode')?.value || '',
      specifiedNationals: this.countriesSelected.filter(c => c !== '') || undefined,
      openingDays: this.form.get('openingDays')?.value,
      openingHours: this.form.get('openingHours')?.value,
      closedDays: this.form.get('closedDays')?.value,
      address: this.form.get('address')?.value || ''
    };

    this.loader.loading(true);
    this.api.updatePlace(place, this.api.env.useGuichet).then(() => this.place.invalidatePlaceCache(this.placeId)).then(() => {
      this.loader.loading(true, {
        type: 'info',
        message: this.lang.transform('place.update.success')
      });
    }).catch(() => {
      this.loader.loading(true, {
        type: 'error',
        message: this.lang.transform('place.update.error')
      });
    });
  }

  private createPlace() {
    const place: Place = {
      longName: this.form.get('name')?.value,
      parent_id: this.form.get('placesParent')?.value,
      externalCode: this.form.get('externalCode')?.value,
      specifiedNationals: this.countriesSelected.filter(c => c !== ''),
      address: this.form.get('address')?.value || '',
      phoneNumbersCsv: this.form.get('phoneNumbersCsv')?.value || '',
      connectUAPattern: this.form.get('connectUAPattern')?.value || '.*',
      connectIPRange: this.form.get('connectIPRange')?.value || '0.0.0.0/0'
    };
    const id = this.form.get('id').value;
    this.api.createPlace(place, id).then(() => {
      this.nav.to('place/' + id);
      this.loader.loading(false);
      this.placeId = id;
      this.ngOnInit();
    }).catch(() => {
      this.submitted = false;
      this.loader.loading(true, {type: 'error', message: this.lang.transform('err.createplace')});
    });
  }

  deletePlace() {
    this.loader.loading(true, {type: 'info', message: this.lang.transform('place.delete'), btnLabel: this.lang.transform('global.confirm')}).then((done: boolean) => {
      if(done) {
        this.api.deletePlace(this.placeId).then(() => {
          this.loader.loading(true, {type: 'info', message: this.lang.transform('place.delete.success')});
          if(this.placeId === this.api.userPlaceId) {
            this.api.signOut();
          } else {
            this.nav.to('places');
          }
        }).catch((err: any) => {
          this.loader.loading(true, {type: 'error', message: err});
        });
      }
    });
  }

  goToPreviousUrl() {
    this.router.navigateByUrl(localStorage.getItem('previous_url'));
  }

  transformSelect(arrayObj: Place[]): { name: string; value: string }[] {
    return arrayObj.map((obj: Place) => ({name: obj.longName, value: obj.id}));
  }

  toggleCountry(value: string, event: any) {
    if(event.checked) {
      this.countriesSelected.push(value);
    } else {
      this.countriesSelected = this.countriesSelected.filter(country => country !== value);
    }
  }

  isSelected(value: string) {
    return this.countriesSelected.includes(value);
  }
}
